import { useState } from 'react'
import { AlertCircle, Check, ClipboardCopy, Loader2, Plus, Sparkles } from 'lucide-react'
import { TEMPLATE_LABELS, type AssistantChatMessage } from '@/lib/types'

interface Props {
  msg: AssistantChatMessage
  /** Pre-fill the chat input with text for the rep to send as their next message. */
  onInsertToInput: (text: string) => void
}

/**
 * AI message bubble. Renders the simplified ideas list with a one-line
 * summary header. Each idea has a copy button + an "insert to next message"
 * button so the rep can pull suggestions back into the chat as their own.
 */
export function AssistantBubble({ msg, onInsertToInput }: Props) {
  // Loading state.
  if (msg.isLoading) {
    return (
      <div className="flex justify-start">
        <div className="flex items-center gap-2 rounded-lg rounded-tl-sm bg-white px-3 py-2 text-[12px] text-zinc-500 shadow-sm ring-1 ring-zinc-200">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          Thinking…
        </div>
      </div>
    )
  }

  // Error state.
  if (msg.error) {
    return (
      <div className="flex justify-start">
        <div className="flex max-w-[90%] items-start gap-2 rounded-lg rounded-tl-sm bg-red-50 px-3 py-2 text-[12px] text-red-800 shadow-sm ring-1 ring-red-200">
          <AlertCircle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
          <div>{msg.error}</div>
        </div>
      </div>
    )
  }

  const s = msg.suggestions
  if (!s) return null

  const hasIdeas = s.ideas.length > 0
  const hasSummary = Boolean(s.summary?.trim())

  return (
    <div className="flex justify-start">
      <div className="w-full max-w-[95%] rounded-lg rounded-tl-sm bg-white shadow-sm ring-1 ring-zinc-200">
        <header className="flex items-center justify-between border-b border-zinc-100 px-3 py-1.5">
          <div className="flex items-center gap-1.5 text-[11px] text-zinc-500">
            <Sparkles className="h-3 w-3" />
            <span>{TEMPLATE_LABELS[msg.template]}</span>
          </div>
          {msg.meta && (
            <div className="font-mono text-[10px] text-zinc-400">{msg.meta.latencyMs} ms</div>
          )}
        </header>

        {hasSummary && (
          <div className="border-b border-zinc-100 bg-zinc-50 px-3 py-2 text-[12px] italic text-zinc-700">
            {s.summary}
          </div>
        )}

        {!hasIdeas ? (
          <div className="px-3 py-3 text-[12px] italic text-zinc-500">
            Not enough yet — keep typing notes and try again.
          </div>
        ) : (
          <ul className="divide-y divide-zinc-100">
            {s.ideas.map((idea, i) => (
              <IdeaRow key={i} text={idea.text} onInsertToInput={onInsertToInput} />
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}

interface IdeaRowProps {
  text: string
  onInsertToInput: (text: string) => void
}

function IdeaRow({ text, onInsertToInput }: IdeaRowProps) {
  const [copied, setCopied] = useState(false)

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch (err) {
      console.warn('[copy] clipboard write failed', err)
    }
  }

  return (
    <li className="group flex items-start gap-2 px-3 py-2 hover:bg-zinc-50">
      <span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-blue-500" />
      <p className="flex-1 text-[13px] leading-relaxed text-zinc-800">{text}</p>
      <div className="flex items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100">
        <button
          type="button"
          onClick={() => onInsertToInput(text)}
          title="Insert into next message"
          className="rounded p-1 text-zinc-500 hover:bg-white hover:text-zinc-900"
        >
          <Plus className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          onClick={handleCopy}
          title="Copy"
          className="rounded p-1 text-zinc-500 hover:bg-white hover:text-zinc-900"
        >
          {copied ? (
            <Check className="h-3.5 w-3.5 text-emerald-600" />
          ) : (
            <ClipboardCopy className="h-3.5 w-3.5" />
          )}
        </button>
      </div>
    </li>
  )
}
